import React, { useEffect, useState } from "react";
import { Empty, message } from "antd";
import GenericStatCard from "../../../Components/Cards/GenericStatCard";
import StackedBarChart from "../../../Components/Charts/StackedBarChart";
import LineChart from "../../../Components/Charts/LineChart";
import Barchart from "../../../Components/Charts/BarChart";
import vehicleIcon from "../../../Assets/Icons/traffic_jam.svg";
import activeVehicleIcon from "../../../Assets/Icons/traffic_jam-active.svg";
import vendorIcon from "../../../Assets/Icons/id_card-vehicles.svg";
import activeIcon from "../../../Assets/Icons/id_card-active.svg";
import fuelIcon from "../../../Assets/Icons/order_approve.svg";
import GenericTable from "../../../Components/Tables/GenericTable";
import { getFleetManagerDashboard } from "../../../API/FleetManager/Dashboard";
import "./style.css";
import InitialsAvatar from "../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const formatCurrency = (value) =>
  `$${(parseFloat(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const Dashboard = () => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [timeFilter, setTimeFilter] = useState("Monthly");
  const [stats, setStats] = useState(null);
  const [creditUtilization, setCreditUtilization] = useState(null);
  const [spendByFuelType, setSpendByFuelType] = useState({
    labels: [],
    spend: [],
  });
  const [fuelConsumptionTrend, setFuelConsumptionTrend] = useState({
    labels: [],
    spend: [],
    volume: [],
  });
  const [topVehiclesByUsage, setTopVehiclesByUsage] = useState([]);
  const [topDriversByCreditUsage, setTopDriversByCreditUsage] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);

  const fetchDashboard = async (period) => {
    try {
      const res = await getFleetManagerDashboard(period);

      if (Math.floor(res?.status / 100) === 2) {
        const data = res?.data?.data;

        setStats(data?.stats || null);
        setCreditUtilization(data?.credit_utilization || null);
        if (!period) {
          setTimeFilter(
            data?.spend_by_fuel_type?.period
              ? data.spend_by_fuel_type.period.charAt(0).toUpperCase() +
                  data.spend_by_fuel_type.period.slice(1)
              : "Monthly",
          );
        }
        setSpendByFuelType({
          labels: data?.spend_by_fuel_type?.by_fuel_type?.labels || [],
          spend: (
            data?.spend_by_fuel_type?.by_fuel_type?.total_amount || []
          ).map((v) => parseFloat(v) || 0),
        });
        setFuelConsumptionTrend({
          labels: data?.fuel_consumption_trend?.labels || [],
          spend: (data?.fuel_consumption_trend?.spend || []).map(
            (v) => parseFloat(v) || 0,
          ),
          volume: (data?.fuel_consumption_trend?.volume || []).map(
            (v) => parseFloat(v) || 0,
          ),
        });
        setTopVehiclesByUsage(data?.top_vehicles_by_usage || []);
        setTopDriversByCreditUsage(data?.top_drivers_by_credit_usage || []);
        setRecentTransactions(data?.recent_transactions || []);
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to load dashboard data."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load dashboard data."),
      );
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTimeFilterClick = (tab) => {
    setTimeFilter(tab);
    fetchDashboard(tab.toLowerCase());
  };

  const used = parseFloat(creditUtilization?.used) || 0;
  const limit = parseFloat(creditUtilization?.limit) || 0;
  const available = parseFloat(creditUtilization?.available) || limit - used;
  const percentageUsed = creditUtilization?.percentage_used ?? 0;

  const vehicleRanking = topVehiclesByUsage.map((item, index) => ({
    key: item.vehicle_id ?? index,
    label: item.vehicle || item.vehicle_name || item.label || "-",
    volume: parseFloat(item.total_volume ?? item.volume) || 0,
  }));
  const maxVehicleVolume = Math.max(
    1,
    ...vehicleRanking.map((v) => v.volume),
  );

  const driverRanking = topDriversByCreditUsage.map((item, index) => ({
    key: item.driver_id ?? index,
    label: item.driver_name || item.name || item.label || "-",
    amount: parseFloat(item.total_amount ?? item.amount) || 0,
  }));
  const maxDriverAmount = Math.max(1, ...driverRanking.map((d) => d.amount));

  const dataSource = recentTransactions.map((item, index) => ({
    key: item.id ?? index,
    date: item.date || item.transaction_date || item.created_at || "-",
    txnId: item.txn_id || item.transaction_id || item.request_id || "-",
    driverName: item.driver_name || item.driver || "-",
    driverAvatar: resolveAvatarUrl(item.driver_avatar),
    vehicleId: item.vehicle_id || item.vehicle_registration || "-",
    vendor: item.fuel_vendor_name || item.vendor_name || item.vendor || "-",
    vendorAvatar: resolveAvatarUrl(item.fuel_vendor_avatar),
    fuelType: item.fuel_type_name || item.fuel_type || "-",
    amount: formatCurrency(item.amount),
    status: item.status
      ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
      : "-",
  }));

  const getUniqueFilters = (data, key) =>
    [...new Set(data.map((item) => item[key]))].map((value) => ({
      text: value,
      value,
    }));

  const fuelTypeFilters = getUniqueFilters(dataSource, "fuelType");
  const statusFilters = getUniqueFilters(dataSource, "status");

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      sorter: (a, b) =>
        (new Date(a.date).getTime() || 0) - (new Date(b.date).getTime() || 0),
    },
    {
      title: "Txn ID",
      dataIndex: "txnId",
      key: "txnId",
      enableSearch: true,
    },

    {
      title: "Driver Name",
      dataIndex: "driverName",
      key: "driverName",
      enableSearch: "true",
      render: (text, record) => (
        <span
          className="manager-dashboard__avatar-cell"
          style={{ color: "#4F46E5" }}
        >
          <InitialsAvatar
            src={record.driverAvatar}
            name={text}
            className="manager-dashboard__avatar"
            style={{ fontSize: 10 }}
          />
          {text}
        </span>
      ),
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicleId",
      key: "vehicleId",
      enableSearch: true,
    },
    {
      title: "Vendor",
      dataIndex: "vendor",
      key: "vendor",
      enableSearch: "true",
      render: (text, record) => (
        <span className="manager-dashboard__avatar-cell">
          <InitialsAvatar
            src={record.vendorAvatar}
            name={text}
            alt="vendor"
            className="manager-dashboard__avatar"
            style={{ fontSize: 10 }}
          />
          {text}
        </span>
      ),
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      filters: fuelTypeFilters,
      onFilter: (value, record) => record.fuelType === value,
      render: (text) => <span className="fuel-type-badge">{text}</span>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) =>
        parseFloat(a.amount.replace(/[^0-9.-]+/g, "")) -
        parseFloat(b.amount.replace(/[^0-9.-]+/g, "")),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Approved" ? "approved" : "denied"}`}
        >
          {text}
        </span>
      ),
    },
  ];

  return (
    <div className="manager-dashboard">
      <div className="stats-container">
        <div className="stats-container-card">
          <GenericStatCard
            title="Total Vehicles"
            value={stats?.total_vehicles?.value ?? 0}
            {...(stats?.total_vehicles?.percentage != null && {
              percentage: `${stats.total_vehicles.percentage}`,
            })}
            isIncrease={stats?.total_vehicles?.is_increase}
            icon={vehicleIcon}
          />
        </div>

        <div className="stats-container-card">
          <GenericStatCard
            title="Active Vehicles"
            value={stats?.active_vehicles?.value ?? 0}
            {...(stats?.active_vehicles?.percentage != null && {
              percentage: `${stats.active_vehicles.percentage}`,
            })}
            isIncrease={stats?.active_vehicles?.is_increase}
            icon={activeVehicleIcon}
          />
        </div>

        <div className="stats-container-card">
          <GenericStatCard
            title="Total Drivers"
            value={stats?.total_drivers?.value ?? 0}
            {...(stats?.total_drivers?.percentage != null && {
              percentage: `${stats.total_drivers.percentage}`,
            })}
            isIncrease={stats?.total_drivers?.is_increase}
            icon={vendorIcon}
          />
        </div>

        <div className="stats-container-card">
          <GenericStatCard
            title="Active Drivers"
            value={stats?.active_drivers?.value ?? 0}
            {...(stats?.active_drivers?.percentage != null && {
              percentage: `${stats.active_drivers.percentage}`,
            })}
            isIncrease={stats?.active_drivers?.is_increase}
            icon={activeIcon}
          />
        </div>
      </div>
      <div className="row two-column-row">
        <div className="tabs card-fuel">
          <GenericStatCard
            title="Fuel Transactions"
            value={stats?.fuel_transactions?.value ?? 0}
            {...(stats?.fuel_transactions?.percentage != null && {
              percentage: `${stats.fuel_transactions.percentage}`,
            })}
            isIncrease={stats?.fuel_transactions?.is_increase}
            subtitle="vs. last month"
            icon={fuelIcon}
          />
        </div>

        <div className="credit-card">
          <h3 className="credit-title">Credit Utilization in JMD</h3>

          <div className="credit-bar-section">
            <span className="label">Available Credits</span>
            <span className="available-amount">
              ${available.toLocaleString()}
            </span>
          </div>

          <div className="credit-bar">
            <div
              className="credit-bar-fill"
              style={{ width: `${percentageUsed}%` }}
            ></div>
          </div>

          <div className="credit-footer">
            <span>
              Used:<span> ${used.toLocaleString()}</span>
            </span>

            <span>
              Limit: <span>${limit.toLocaleString()}</span>
            </span>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="graph-card">
          <div className="chart-controls-header-simple">
            <div className="time-filter-pills">
              {["Daily", "Monthly", "Yearly"].map((tab) => (
                <button
                  key={tab}
                  className={`pill-button ${timeFilter === tab ? "active" : ""}`}
                  onClick={() => handleTimeFilterClick(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="bar-chart-container fueldata-graph">
            <Barchart
              graphData={{
                labels: spendByFuelType.labels,
                spend: spendByFuelType.spend,
              }}
              lengthKeys={1}
              colourTheme={["#4F46E5"]}
              graphTitle="Spend By Fuel Type"
              selectTenByDefault={false}
              units={["$"]}
              classNameCSS="quantity-to-use"
              grayzoned={true}
            />
          </div>
        </div>
      </div>
      <div className="row">
        <div className="graph">
          <LineChart
            graphData={{
              labels: fuelConsumptionTrend.labels,
              volume: fuelConsumptionTrend.volume,
              spend: fuelConsumptionTrend.spend,
            }}
            lengthKeys={2}
            colourTheme={["#F59E0B", "#4F46E5"]}
            graphTitle="Fuel Consumption Trends"
            selectTenByDefault={false}
            units={["L", "$"]}
            classNameCSS="fuel-spend"
          />
        </div>
      </div>

      <div className="row">
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-title">Top Vehicles by Fuel Usage</div>
          </div>

          {vehicleRanking.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
          ) : vehicleRanking.map((vehicle) => (
            <div className="stat-item" key={vehicle.key}>
              <div className="stat-label">{vehicle.label}</div>
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill progress-green"
                    style={{
                      width: `${(vehicle.volume / maxVehicleVolume) * 100}%`,
                    }}
                  ></div>
                </div>
                <div className="stat-value">
                  {vehicle.volume.toLocaleString()} L
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="dashboard-card">
          <div className="card-header">
            <div className="card-title">Top Drivers by Credits Usage</div>
          </div>

          {driverRanking.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data" />
          ) : driverRanking.map((driver) => (
            <div className="stat-item" key={driver.key}>
              <div className="stat-label">{driver.label}</div>
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill progress-purple"
                    style={{
                      width: `${(driver.amount / maxDriverAmount) * 100}%`,
                    }}
                  ></div>
                </div>
                <div className="stat-value">
                  {formatCurrency(driver.amount)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="fuel-table">
        <GenericTable
          title="Fuel Transactions"
          dataSource={dataSource}
          columns={columns}
          onSelectionChange={(keys, rows) => setSelectedRows(rows)}
          onRowClick={(record) => console.log("Row clicked:", record)}
        />
      </div>
    </div>
  );
};

export default Dashboard;
